import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";
import { searchCompanyDrivers, unlinkDriverFromCarrier } from "@/lib/api";
import { DriverList, driverName } from "@/components/drivers/DriverList";
import { AddDriverDialog } from "@/components/drivers/AddDriverDialog";

/** Carrier users: their company's drivers — search, add new, remove from list. */
export default function MyDrivers() {
  const { assignments = [] } = useAuth();
  const company = assignments.find((a) => a.role === "approved_booker" && a.approved_booker_id);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback((q) => searchCompanyDrivers({ q, limit: 100 }), []);

  const remove = async () => {
    const { error } = await unlinkDriverFromCarrier(company.approved_booker_id, removing.id);
    setRemoving(null);
    if (error) { toast.error(error.message); return; }
    toast.success("Removed from your drivers.");
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="space-y-6" data-testid="my-drivers-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Users className="h-6 w-6" /> My drivers</h1>
          <p className="text-sm text-slate-500 mt-1">Drivers you can pick when booking. Only your company sees this list.</p>
        </div>
        <Button onClick={() => setAdding(true)} disabled={!company} data-testid="my-drivers-add-btn"><Plus className="h-4 w-4 mr-1" /> Add driver</Button>
      </div>
      {!company ? (
        <p className="text-sm text-slate-500" data-testid="my-drivers-no-company">Your login isn&apos;t linked to a carrier company yet — ask the site team.</p>
      ) : (
        <DriverList load={load} reloadKey={reloadKey} testid="my-drivers"
                    empty="No drivers yet. Add one, or they'll appear after you assign them to a booking."
                    actions={(d) => (
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setRemoving(d)}
                              data-testid={`my-drivers-remove-${d.id}`}>Remove</Button>
                    )} />
      )}
      <AddDriverDialog open={adding} onOpenChange={setAdding} clientId={company?.client_id}
                       onAdded={() => setReloadKey((k) => k + 1)} />
      <AlertDialog open={!!removing} onOpenChange={(v) => !v && setRemoving(null)}>
        <AlertDialogContent data-testid="my-drivers-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing && driverName(removing)}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll drop off your list. Their login and any existing bookings stay as they are.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="my-drivers-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="my-drivers-remove-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
